import { apiGateway } from '@va/api-client';
import {
  ActivateCompanyDomainInput,
  ActivateSenderEmailsInput,
  ActivateSenderEmailsResponsePayload,
  ActivateWebsiteDomainInput,
  CancelPendingCompanyDomainInput,
  CancelPendingSenderEmailsInput,
  CancelPendingWebsiteDomainInput,
  CheckDNSInput,
  CheckDNSResponsePayload,
  CreateCompanyDomainInput,
  CreateOrUpdateAgencyCustomSubdomainInput,
  CreateSenderEmailInput,
  CreateWebsiteDomainInput,
  CustomDomainRecord,
  DeleteCompanyDomainInput,
  DeleteSenderEmailsInput,
  DeleteSenderEmailsResponsePayload,
  DeleteWebsiteDomainInput,
  DNSRecord,
  GetTargetedDNSRecordsInput,
  ListCompanyDomainsByTypeInput,
  ListCompanyDomainsByTypeResponsePayload,
  ListDNSRecordsInput,
  ListDNSRecordsResponsePayload,
  ListSenderEmailsInput,
  ListSenderEmailsResponsePayload,
  ListWebsiteDomainsByTypeInput,
  ListWebsiteDomainsByTypeResponsePayload,
  PayloadResponse,
  SenderEmail,
  UpdateCustomSenderNameInput,
} from './custom-domains.api.types';

// If agency UI doesn't exist it will create it automatically, otherwise it will update it and clean up / delete any previous custom domains if they exist
export const createOrUpdateAgencyCustomSubdomain = (input: CreateOrUpdateAgencyCustomSubdomainInput) => {
  return apiGateway.post<PayloadResponse<{ success: boolean }>>(`/v1/companies/${input.agencyId}/custom-subdomains`, {
    customUrl: input.customUrl,
    platformType: 'awl',
    type: 'dashboard',
  });
};

export const cancelPendingCompanyDomain = (input: CancelPendingCompanyDomainInput) => {
  return apiGateway.post<PayloadResponse<{ success: boolean }>>(
    `/v1/companies/${input.companyId}/custom-domains/${input.customDomainId}/cancel`,
  );
};

export const cancelPendingWebsiteDomain = (input: CancelPendingWebsiteDomainInput) => {
  return apiGateway.post<PayloadResponse<{ success: boolean }>>(
    `/v1/websites/${input.websiteId}/custom-domains/${input.customDomainId}/cancel`,
  );
};

export const deleteCompanyDomain = (input: DeleteCompanyDomainInput) => {
  return apiGateway.post<PayloadResponse<{ success: boolean }>>(
    `/v1/companies/${input.companyId}/custom-domains/${input.customDomainId}/delete`,
  );
};

export const deleteWebsiteDomain = (input: DeleteWebsiteDomainInput) => {
  return apiGateway.post<PayloadResponse<{ success: boolean }>>(
    `/v1/websites/${input.websiteId}/custom-domains/${input.customDomainId}/delete`,
  );
};

export const activateCompanyDomain = (input: ActivateCompanyDomainInput) => {
  return apiGateway.post<PayloadResponse<{ success: boolean }>>(
    `/v1/companies/${input.companyId}/custom-domains/${input.customDomainId}/activate`,
  );
};

export const activateWebsiteDomain = (input: ActivateWebsiteDomainInput) => {
  return apiGateway.post<PayloadResponse<{ success: boolean }>>(
    `/v1/websites/${input.websiteId}/custom-domains/${input.customDomainId}/activate`,
  );
};

export const createCompanyDomain = (input: CreateCompanyDomainInput) => {
  return apiGateway.post<PayloadResponse<CustomDomainRecord>>(`/v1/companies/${input.companyId}/custom-domains`, {
    domain: input.domain,
    platform: input.platform,
    type: input.type,
    skipActivation: input.skipActivation,
  });
};

export const createWebsiteDomain = (input: CreateWebsiteDomainInput) => {
  return apiGateway.post<PayloadResponse<CustomDomainRecord>>(`/v1/websites/${input.websiteId}/custom-domains`, {
    domain: input.domain,
    platform: input.platform,
    type: input.type,
    skipActivation: input.skipActivation,
  });
};

export const listCompanyDomainsByType = (input: ListCompanyDomainsByTypeInput) => {
  return apiGateway.post<PayloadResponse<ListCompanyDomainsByTypeResponsePayload>>(
    `/v1/companies/${input.companyId}/custom-domains/by-type`,
    {
      type: input.type,
    },
  );
};

export const listWebsiteDomainsByType = (input: ListWebsiteDomainsByTypeInput) => {
  return apiGateway.post<PayloadResponse<ListWebsiteDomainsByTypeResponsePayload>>(
    `/v1/websites/${input.websiteId}/custom-domains/by-type`,
    {
      type: input.type,
    },
  );
};

export const getTargetedDNSRecords = (input: GetTargetedDNSRecordsInput) => {
  return apiGateway.post<PayloadResponse<DNSRecord[]>>(`/v1/dns/records/${input.target}`, {
    platform: input.platform,
    domain: input.domain,
  });
};

export const createSenderEmail = (i: CreateSenderEmailInput) => {
  return apiGateway.post<PayloadResponse<{ dnsRecords: DNSRecord[]; senderEmail: SenderEmail }>>(
    `/v1/companies/${i.companyId}/sender-emails`,
    { email: i.email, senderName: i.senderName, type: i.type, skipActivation: i.skipActivation },
  );
};

export const listSenderEmails = (i: ListSenderEmailsInput) => {
  return apiGateway.get<PayloadResponse<ListSenderEmailsResponsePayload>>(`/v1/companies/${i.companyId}/sender-emails`);
};

export const deleteSenderEmail = (i: DeleteSenderEmailsInput) => {
  return apiGateway.post<PayloadResponse<DeleteSenderEmailsResponsePayload>>(
    `/v1/companies/${i.companyId}/sender-emails/${i.senderEmailId}/delete`,
  );
};

export const activateSenderEmail = (i: ActivateSenderEmailsInput) => {
  return apiGateway.post<PayloadResponse<ActivateSenderEmailsResponsePayload>>(
    `/v1/companies/${i.companyId}/sender-emails/${i.senderEmailId}/activate`,
  );
};

export const cancelPendingSenderEmail = (i: CancelPendingSenderEmailsInput) => {
  return apiGateway.post<PayloadResponse<{ success: boolean }>>(
    `/v1/companies/${i.companyId}/sender-emails/${i.senderEmailId}/cancel`,
  );
};

export const updateCustomSenderName = (i: UpdateCustomSenderNameInput) => {
  return apiGateway.patch<PayloadResponse<{ success: boolean }>>(
    `/v1/companies/${i.companyId}/sender-emails/${i.senderEmailId}`,
    {
      senderName: i.senderName,
    },
  );
};

export type SendDummyEmailInput = { companyId: string; platform: 'AAAS' | 'AWL'; locale: string; emailTo: string };

export const sendDummyEmail = (i: SendDummyEmailInput) => {
  return apiGateway.post<PayloadResponse<{ success: boolean }>>(`/api/companies/${i.companyId}/emails/dummy-email`, {
    emailTo: i.emailTo,
    locale: i.locale,
    platform: i.platform,
  });
};

export const listDNSRecords = (input: ListDNSRecordsInput) => {
  return apiGateway.post<PayloadResponse<ListDNSRecordsResponsePayload>>(`/dns/records`, {
    agencyId: input.agencyId,
    domain: input.domain,
    platform: input.platform,
  });
};

export const checkDNS = async (input: CheckDNSInput) => {
  const res = await apiGateway.post<PayloadResponse<CheckDNSResponsePayload>>(`/v1/dns/check`, {
    host: input.host,
    expectedValue: input.expectedValue,
    type: input.type,
  });
  if (!res.payload.success) {
    throw new Error(res.payload?.message);
  }

  return res;
};
